import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MessageCircle, ArrowLeft, CheckCircle, Cpu, ChevronDown, Check, RefreshCw, Lightbulb } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';
import ChatUI from '../../components/ChatUI';
import { llmModels } from '../../data/dummyData';

export default function StepTest() {
  const { goNext, goPrev } = useOutletContext();
  const {
    currentAgent,
    chatHistory,
    fetchChatHistory,
    sendTestMessage,
    clearChatHistory,
    updateAgentModel,
    isLoading,
    isClearing,
    error,
    clearError
  } = useAgent();

  // Fetch chat history on mount
  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory]);

  const [showModelSelector, setShowModelSelector] = useState(false);
  const [selectedModel, setSelectedModel] = useState(currentAgent?.model || 'openai/gpt-4o-mini');
  const [isSavingModel, setIsSavingModel] = useState(false);

  const currentModel = llmModels.find(m => m.id === selectedModel) || llmModels[0];

  const handleModelChange = async (modelId) => {
    setSelectedModel(modelId);
    setShowModelSelector(false);

    try {
      setIsSavingModel(true);
      await updateAgentModel({ model: modelId });
    } catch (err) {
      console.error('Failed to save model:', err);
      // Revert on error
      if (currentAgent?.model) {
        setSelectedModel(currentAgent.model);
      }
    } finally {
      setIsSavingModel(false);
    }
  };

  const handleSendMessage = async (text) => {
    try {
      await sendTestMessage(text);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleClear = () => {
    clearChatHistory();
  };

  // Transform chat history for ChatUI component
  const messages = chatHistory.map(msg => ({
    id: msg.id,
    type: msg.role === 'user' ? 'user' : 'ai',
    text: msg.content,
    time: msg.time
  }));

  // Model selector component for ChatUI header
  const modelSelector = (
    <div className="relative">
      <button
        onClick={() => setShowModelSelector(!showModelSelector)}
        disabled={isSavingModel}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-border rounded-lg
                   hover:border-primary/30 transition-colors disabled:opacity-50 text-sm"
      >
        {isSavingModel ? (
          <RefreshCw className="w-3.5 h-3.5 text-primary animate-spin" />
        ) : (
          <Cpu className="w-3.5 h-3.5 text-primary" />
        )}
        <span className="font-medium text-text-primary">{currentModel.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-text-muted transition-transform ${showModelSelector ? 'rotate-180' : ''}`} />
      </button>

      {showModelSelector && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-elevated border border-border z-50 overflow-hidden animate-slide-up">
          <div className="p-3 border-b border-border bg-gray-50">
            <p className="text-xs font-medium text-text-muted uppercase">Change AI Model</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {llmModels.map((model) => (
              <button
                key={model.id}
                onClick={() => handleModelChange(model.id)}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors flex items-start gap-3
                           ${selectedModel === model.id ? 'bg-primary/5' : ''}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0
                               ${selectedModel === model.id ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                  {selectedModel === model.id && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary text-sm">{model.name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${model.badgeColor}`}>{model.badge}</span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">{model.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-8 max-w-3xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Test Your AI</h1>
            <p className="text-text-secondary">Try a conversation with your new agent</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
          <button onClick={clearError} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Chat Interface */}
      <div className="h-[500px]">
        <ChatUI
          messages={messages}
          onSendMessage={handleSendMessage}
          onClear={handleClear}
          aiName={currentAgent?.name || 'AI Agent'}
          helperText="Send a message to test how your AI responds. Try asking about your products or services!"
          placeholder="Type a test message..."
          isLoading={isLoading}
          headerRight={modelSelector}
          isClearing={isClearing}
        />
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
        <h4 className="text-sm font-medium text-primary mb-2 flex items-center gap-2">
          <Lightbulb className="w-4 h-4" />
          Testing Tips
        </h4>
        <ul className="text-sm text-text-secondary space-y-1 list-disc pl-5">
          <li>Try asking common customer questions</li>
          <li>Test edge cases and unusual requests</li>
          <li>Check if the tone matches your expectations</li>
          <li>You can refine the behavior prompt later</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button 
          variant="ghost" 
          icon={ArrowLeft}
          onClick={goPrev}
        >
          Back
        </Button>
        <Button 
          icon={CheckCircle}
          iconPosition="left"
          onClick={goNext}
        >
          Looks Good – Continue
        </Button>
      </div>
    </div>
  );
}
